import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  BillingOrgSummary,
  BillingOrgDetail,
  BillingInvoice,
  BillingManager,
  BillingTerm,
  InvoiceFilter,
  InviteResult,
  OnboardRequest,
  OnboardResult,
  RenewalRunSummary,
  TermInput,
  TermKind,
  TrialInput,
  UpcomingRenewal,
} from '../types/billing';

/**
 * Platform billing console — /api/billing (billing_manager or super_admin).
 * Customer-facing subscription calls stay in subscription.ts.
 *
 * Every call resolves to the payload or throws — a `success: false` body
 * becomes an Error carrying the server's message, the same shape the axios
 * interceptor gives a non-2xx.
 */
async function unwrap<T>(p: Promise<ApiResponse<T>>): Promise<T> {
  const res = await p;
  if (!res.success) throw new Error(res.error?.message || 'Request failed');
  return res.data;
}

class BillingService {
  me() {
    return unwrap(apiService.get<{ can_bill: boolean; is_super_admin: boolean }>('/billing/me'));
  }

  listOrganisations(search?: string) {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return unwrap(apiService.get<BillingOrgSummary[]>(`/billing/organisations${qs}`));
  }

  getOrganisation(orgId: number) {
    return unwrap(apiService.get<BillingOrgDetail>(`/billing/organisations/${orgId}`));
  }

  updateOrganisation(orgId: number, data: { billing_email?: string; contact_email?: string; contact_phone?: string; abn?: string }) {
    return unwrap(apiService.patch(`/billing/organisations/${orgId}`, data));
  }

  inviteAdmin(orgId: number, email: string) {
    return unwrap(apiService.post<InviteResult>(`/billing/organisations/${orgId}/invite-admin`, { email }));
  }

  onboard(data: OnboardRequest) {
    return unwrap(apiService.post<OnboardResult>('/billing/onboard', data));
  }

  addTerm(orgId: number, term: TermInput) {
    return unwrap(apiService.post<BillingTerm>(`/billing/organisations/${orgId}/terms`, term));
  }

  revokeTerm(termId: number, reason?: string) {
    return unwrap(apiService.delete(`/billing/terms/${termId}`, { reason }));
  }

  startTrial(orgId: number, trial: TrialInput) {
    return unwrap(apiService.post(`/billing/organisations/${orgId}/trials`, trial));
  }

  extendTrial(subscriptionId: number, trialEndsOn: string, reason?: string) {
    return unwrap(apiService.patch(`/billing/subscriptions/${subscriptionId}/trial`, { trial_ends_on: trialEndsOn, reason }));
  }

  suspend(subscriptionId: number, reason: string) {
    return unwrap(apiService.post(`/billing/subscriptions/${subscriptionId}/suspend`, { reason }));
  }

  reinstate(subscriptionId: number) {
    return unwrap(apiService.post(`/billing/subscriptions/${subscriptionId}/reinstate`, {}));
  }

  cancel(subscriptionId: number, organisationId: number, reason: string) {
    return unwrap(apiService.post(`/billing/subscriptions/${subscriptionId}/cancel`, { organisation_id: organisationId, reason }));
  }

  setRenewal(subscriptionId: number, data: { enabled: boolean; next_billing_date?: string; billing_cycle?: 'monthly' | 'annual' }) {
    return unwrap(apiService.patch(`/billing/subscriptions/${subscriptionId}/renewal`, data));
  }

  /** Operator seat change — no invoice. The customer's own path is subscriptionService.addSeats. */
  setSeatsWithoutInvoice(subscriptionId: number, organisationId: number, seats: { type_key: string; total_seats: number }[]) {
    return unwrap(apiService.patch(`/subscriptions/${subscriptionId}/seats`, { seats, organisation_id: organisationId }));
  }

  listInvoices(filter: InvoiceFilter) {
    return unwrap(apiService.get<BillingInvoice[]>(`/billing/invoices?filter=${filter}`));
  }

  retryInvoice(transactionId: number) {
    return unwrap(apiService.post(`/xero/retry-invoice/${transactionId}`, {}));
  }

  syncInvoiceStatuses() {
    return unwrap(apiService.post('/xero/sync-statuses', {}));
  }

  upcomingRenewals(days = 30) {
    return unwrap(apiService.get<UpcomingRenewal[]>(`/billing/renewals/upcoming?days=${days}`));
  }

  runRenewals(organisationId?: number) {
    return unwrap(apiService.post<RenewalRunSummary>('/billing/renewals/run', organisationId ? { organisation_id: organisationId } : {}));
  }

  listManagers() {
    return unwrap(apiService.get<BillingManager[]>('/billing/managers'));
  }

  grantManager(email: string) {
    return unwrap(apiService.post('/billing/managers', { email }));
  }

  revokeManager(userId: number) {
    return unwrap(apiService.delete(`/billing/managers/${userId}`));
  }
}

/** One line describing a term, for lists and confirmations. */
export function describeTerm(t: { kind: TermKind; value: unknown; plan_name?: string | null; ends_on?: string | null }): string {
  const scope = t.plan_name || 'all modules';
  const until = t.ends_on ? ` until ${t.ends_on}` : '';
  if (t.kind === 'free_period') return `Free: ${scope}${until}`;
  if (t.kind === 'discount_percent') return `${Number(t.value)}% off ${scope}${until}`;
  return `$${Number(t.value).toFixed(2)}/month ex GST off ${scope}${until}`;
}

/** Xero's invoice view for an invoice id. */
export function xeroInvoiceUrl(invoiceId: string): string {
  return `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(invoiceId)}`;
}

export default new BillingService();
